import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { AnalyticsStreamProvider } from '@/contexts/AnalyticsStreamContext';
import { Toaster } from '@/components/ui/sonner';
import { AnalyticsToastProvider } from '@/components/AnalyticsToastProvider';
import { ErrorBoundary, NotFoundPage } from '@/pages/ErrorPage';
import { ExpiredTrialSurvey } from '@/components/ExpiredTrialSurvey';
import { trackPageView } from '@/lib/ga4';

// Pages
import LoginPage from '@/pages/LoginPage';
import SignUpPage from '@/pages/SignUpPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import AcceptInvitationPage from '@/pages/AcceptInvitationPage';
import DashboardLayout from '@/layouts/DashboardLayout';
import OverviewPage from '@/pages/OverviewPage';
import ConversationsPage from '@/pages/ConversationsPage';
import TrendingTopicsPage from '@/pages/TrendingTopicsPage';
import FeedbackPage from '@/pages/FeedbackPage';
import UserGroupsPage from '@/pages/UserGroupsPage';
import UsagePage from '@/pages/UsagePage';
import TokenUsagePage from '@/pages/TokenUsagePage';
import KnowledgeBasePage from '@/pages/KnowledgeBasePage';
import KBManagementPage from '@/pages/KBManagementPage';
import TopicManagementPage from '@/pages/TopicManagementPage';
import ReportsPage from '@/pages/ReportsPage';
import ConnectorsPage from '@/pages/ConnectorsPage';
import SharePointMicrosoftCallback from '@/pages/SharePointMicrosoftCallback';
import TeamManagementPage from '@/pages/TeamManagementPage';
import TenantSettingsPage from '@/pages/TenantSettingsPage';
import TenantManagementPage from '@/pages/TenantManagementPage';
import TenantEditorPage from '@/pages/TenantEditorPage';
import ChatThreadsPage from '@/pages/ChatThreadsPage';
import ContentGapsPage from '@/pages/ContentGapsPage';
import ProfileSettingsPage from '@/pages/ProfileSettingsPage';
import SecuritySettingsPage from '@/pages/SecuritySettingsPage';
import ThemeCustomizerPage from '@/pages/ThemeCustomizerPage';
import IntegrationsPage from '@/pages/IntegrationsPage';
import BillingPage from '@/pages/BillingPage';
import AuditLogPage from '@/pages/AuditLogPage';
import PricingConfigPage from '@/pages/PricingConfigPage';
import LeaderboardPage from '@/pages/LeaderboardPage';
import NotificationsPage from '@/pages/NotificationsPage';
import EmployeeDetailPage from '@/pages/EmployeeDetailPage';
import EmployeesListPage from '@/pages/EmployeesListPage';
import AcceptSuperadminInvitationPage from '@/pages/AcceptSuperadminInvitationPage';
import ApplicationLogsPage from '@/pages/ApplicationLogsPage';
import ReferralPage from '@/pages/ReferralPage';
import ReferralManagementPage from '@/pages/ReferralManagementPage';
import SSOPage from '@/pages/SSOPage';
import AdminSsoAuthorizePage from '@/pages/AdminSsoAuthorizePage';

// Loading screen component
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <div className="relative">
          <div className="w-12 h-12 rounded-full border-4 border-muted animate-pulse"></div>
          <div className="absolute inset-0 w-12 h-12 rounded-full border-4 border-transparent border-t-primary animate-spin"></div>
        </div>
        <p className="text-muted-foreground text-sm">Loading...</p>
      </div>
    </div>
  );
}

// Protected Route wrapper
export function shouldBlockExpiredTrial({ tenant, isAdmin, isSuperadmin, viewingAsSuperadmin, pathname }) {
  const isTrialExpired = tenant?.subscription_status === 'trial' &&
    (tenant?.trial_expired || (tenant?.trial_days_remaining ?? 0) <= 0);

  if (!isTrialExpired) {
    return false;
  }

  // Belt-and-suspenders (EIP-340): subscription_state comes from the backend's
  // real entitlement gate (evaluate_subscription_access), which is
  // provenance-aware — a tenant with a live stripe_subscription_id is never
  // judged against trial_ends_at even though subscription_status still reads
  // 'trial'. Never block someone the backend considers 'ok'. This also covers
  // an already-logged-in session whose cached tenant object predates the
  // backend fix, since the frontend deploys independently of the backend.
  if (tenant?.subscription_state === 'ok') {
    return false;
  }

  if (isSuperadmin && viewingAsSuperadmin) {
    return false;
  }

  // EIP-359: profile settings is where force_password_change sends people (see
  // the redirect in ProtectedRoute below), and the password form there is the
  // only thing the backend will accept while that flag is set. Blocking the
  // page here rendered the trial survey in its place, so the form never
  // mounted, and the survey's own submit 403s at the force-password gate
  // anyway. Same exemption, and the same reason, as
  // shouldBlockForPasskeyEnrollment below.
  //
  // Unconditional rather than gated on user.force_password_change: that flag
  // rides a cached user payload that can be stale or absent while the JWT claim
  // is live, so a conditional exemption fails closed, straight back into the
  // lockout. The hole is one page wide and every write on it is still refused
  // server-side; this gate was never the fence.
  const isProfilePage = pathname.includes('/settings/profile');
  const isBillingPage = pathname.includes('/settings/billing');
  return !(isProfilePage || (isAdmin && isBillingPage));
}

/**
 * Blocks a superadmin who has not finished enrolling passkeys (EIP-320).
 *
 * Fails OPEN unless the flag is strictly true. The field is absent from any user
 * payload cached before this shipped, and from any backend that has not
 * deployed it yet — neither must be able to strand someone on the enrollment
 * page. Reading a missing field as "enrollment needed" would turn a stale
 * localStorage entry into a lockout.
 *
 * Reads `user.is_superadmin`, never `hasRole(['superadmin'])`. Superadmins are
 * minted with platform_role "admin" and hasRole only ever compares against
 * platform_role, so `hasRole(['superadmin'])` is unconditionally false —
 * navigation.js:77 and TenantSettingsPage.js:118 are already dead branches
 * because of it.
 *
 * The backend decides WHETHER enrollment is required; this only decides where to
 * send someone. Keeping the predicate on the server is what stops a direct API
 * call from bypassing the control — the gate here is a signpost, not a fence.
 */
export function shouldBlockForPasskeyEnrollment({ user, pathname }) {
  if (user?.passkey_enrollment_required !== true) {
    return false;
  }
  if (!user?.is_superadmin) {
    return false;
  }
  // The destination must not block itself, and force_password_change redirects
  // to profile — a user carrying both flags has to be able to land there.
  if (pathname.includes('/settings/security') || pathname.includes('/settings/profile')) {
    return false;
  }
  return true;
}

function ProtectedRoute({ children }) {
  const { user, loading, tenant, hasRole, isSuperadmin, viewingAsSuperadmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const isAdmin = hasRole(['admin']);
  const isProfilePage = location.pathname.includes('/settings/profile');

  if (user.force_password_change && !isProfilePage) {
    return <Navigate to="/settings/profile" replace />;
  }

  // After force_password_change: a user carrying both flags lands on profile
  // first, and ProfileSettingsPage clears that flag on success, so this takes
  // over on the next render.
  //
  // Before the trial check: a platform superadmin is not a tenant subscriber and
  // must not be diverted into a tenant's expired-trial survey on the way to
  // enrolling.
  //
  // <Navigate> rather than rendering in place, because enrollment bounces the
  // user out to an OS or password-manager dialog and they may reload — the gate
  // needs a real, refreshable URL to come back to.
  if (shouldBlockForPasskeyEnrollment({ user, pathname: location.pathname })) {
    return <Navigate to="/settings/security" replace />;
  }

  if (shouldBlockExpiredTrial({
    tenant,
    isAdmin,
    isSuperadmin,
    viewingAsSuperadmin,
    pathname: location.pathname,
  })) {
    return <ExpiredTrialSurvey />;
  }

  return children;
}

// Role-gated Route wrapper — redirects to /overview if user lacks required role
function RoleRoute({ roles, children }) {
  const { hasRole } = useAuth();

  if (!hasRole(roles)) {
    return <Navigate to="/overview" replace />;
  }

  return children;
}

function SuperadminRoute({ children }) {
  const { isSuperadmin } = useAuth();

  if (!isSuperadmin) {
    return <Navigate to="/overview" replace />;
  }

  return children;
}

// Public Route wrapper
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (user) {
    // A fresh sign-in sets `user` via setSession, which re-renders THIS
    // component on the same tick LoginPage's own handler is still running --
    // its imperative navigate(next) call and this component's declarative
    // redirect are two independent writers to the same history entry, and
    // whichever commits second silently wins. EIP-330 found this the hard
    // way: this used to redirect unconditionally to "/", so a `next` bound
    // for /admin-sso/authorize collapsed to /overview roughly half the time.
    // Computing the identical target here (next, else force_password_change,
    // else "/") makes the race harmless instead of chasing its timing --
    // whichever writer wins, the destination is now the same one.
    if (user.force_password_change) {
      return <Navigate to="/settings/profile" replace />;
    }
    const params = new URLSearchParams(location.search);
    // Microsoft's OAuth redirect_uri can't carry ?next= (it must exactly
    // match what's registered with the IdP), so LoginPage stashes it in
    // sessionStorage before that hop instead -- see handleMicrosoftLogin.
    const next = params.get('next') || sessionStorage.getItem('eva_login_next');
    sessionStorage.removeItem('eva_login_next');
    const target = next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
    return <Navigate to={target} replace />;
  }

  return children;
}

// Fires a GA4 page_view on every client-side route change -- the whole app,
// authenticated dashboard routes included. lib/ga4.js decides whether anything
// is sent (prod host only) and what (masked path, no query).
//
// Redirect hops count twice by design: "/" -> <Navigate to="/overview" replace>
// is two pathname changes and therefore two page_views, and so are the
// PublicRoute/ProtectedRoute bounces. Not filtered here -- the redirect target
// is the page the user actually saw, and the hop is cheap to exclude in GA4 if
// it ever skews a report.
function usePageTracking() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
}

function AppRoutes() {
  usePageTracking();
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignUpPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/accept-invitation"
        element={
          <PublicRoute>
            <AcceptInvitationPage />
          </PublicRoute>
        }
      />
      <Route
        path="/verify-email"
        element={
          <PublicRoute>
            <VerifyEmailPage />
          </PublicRoute>
        }
      />
      <Route
        path="/accept-superadmin-invitation"
        element={
          <PublicRoute>
            <AcceptSuperadminInvitationPage />
          </PublicRoute>
        }
      />
      <Route path="/sso" element={<SSOPage />} />
      <Route path="/connectors/microsoft/callback" element={<SharePointMicrosoftCallback />} />
      {/* eva-website admin panel cross-origin handoff (EIP-330). Not wrapped in
          PublicRoute/ProtectedRoute -- both redirect via <Navigate> and drop the
          query string, which would discard return_url/state. The page does its
          own auth branching instead. */}
      <Route path="/admin-sso/authorize" element={<AdminSsoAuthorizePage />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        {/* Default redirect to overview */}
        <Route index element={<Navigate to="/overview" replace />} />
        
        {/* Overview (Landing Page) */}
        <Route path="overview" element={<OverviewPage />} />
        
        {/* Conversations */}
        <Route path="conversations/activity" element={<ConversationsPage />} />
        <Route path="conversations/trending" element={<TrendingTopicsPage />} />
        <Route path="conversations/feedback" element={<FeedbackPage />} />
        <Route path="conversations/threads" element={<SuperadminRoute><ChatThreadsPage /></SuperadminRoute>} />
        
        {/* People */}
        <Route path="people/employees" element={<RoleRoute roles={['admin', 'approver', 'analyst', 'superadmin']}><EmployeesListPage /></RoleRoute>} />
        <Route path="people/employees/:userId" element={<RoleRoute roles={['admin', 'approver', 'analyst', 'superadmin']}><EmployeeDetailPage /></RoleRoute>} />
        <Route path="people/groups" element={<RoleRoute roles={['admin', 'approver']}><UserGroupsPage /></RoleRoute>} />
        <Route path="people/content-team" element={<RoleRoute roles={['admin', 'approver', 'analyst']}><TeamManagementPage /></RoleRoute>} />
        <Route path="people/adoption" element={<UsagePage />} />
        
        {/* Knowledge Base */}
        <Route path="kb/content" element={<RoleRoute roles={['admin', 'approver', 'analyst']}><KBManagementPage /></RoleRoute>} />
        <Route path="kb/coverage" element={<KnowledgeBasePage />} />
        <Route path="kb/topics" element={<RoleRoute roles={['admin', 'approver']}><TopicManagementPage /></RoleRoute>} />
        <Route path="kb/gaps" element={<RoleRoute roles={['admin', 'approver', 'analyst']}><ContentGapsPage /></RoleRoute>} />
        <Route path="kb/explorer" element={<Navigate to="/kb/coverage" replace />} />
        <Route path="kb/management" element={<Navigate to="/kb/content" replace />} />
        
        {/* Reports */}
        <Route path="reports" element={<RoleRoute roles={['admin', 'approver', 'analyst']}><ReportsPage /></RoleRoute>} />
        
        {/* Connectors */}
        <Route path="connectors" element={<RoleRoute roles={['admin']}><ConnectorsPage /></RoleRoute>} />
        
        {/* Settings */}
        <Route path="settings/organization" element={<RoleRoute roles={['admin', 'approver']}><TenantSettingsPage /></RoleRoute>} />
        <Route path="settings/profile" element={<ProfileSettingsPage />} />
        {/* Plain protected route on purpose — see SecuritySettingsPage. A
            superadmin mid-enrollment must be able to reach this. */}
        <Route path="settings/security" element={<SecuritySettingsPage />} />
        <Route path="settings/billing" element={<RoleRoute roles={['admin']}><BillingPage /></RoleRoute>} />
        <Route path="settings/usage" element={<RoleRoute roles={['admin', 'analyst']}><TokenUsagePage /></RoleRoute>} />
        <Route path="settings/audit-log" element={<RoleRoute roles={['admin', 'approver']}><AuditLogPage /></RoleRoute>} />
        
        {/* Engagement */}
        <Route path="engagement/leaderboard" element={<LeaderboardPage />} />
        <Route path="engagement/notifications" element={<RoleRoute roles={['admin', 'approver']}><NotificationsPage /></RoleRoute>} />
        <Route path="engagement/employees/:userId" element={<EmployeeDetailPage />} />
        
        {/* Referral Program */}
        <Route path="referrals" element={<ReferralPage />} />
        
        {/* Superadmin */}
        <Route path="superadmin/tenants" element={<SuperadminRoute><TenantManagementPage /></SuperadminRoute>} />
        <Route path="superadmin/tenants/:tenantId" element={<SuperadminRoute><TenantEditorPage /></SuperadminRoute>} />
        <Route path="superadmin/theme" element={<SuperadminRoute><ThemeCustomizerPage /></SuperadminRoute>} />
        <Route path="superadmin/integrations" element={<SuperadminRoute><IntegrationsPage /></SuperadminRoute>} />
        <Route path="superadmin/pricing" element={<SuperadminRoute><PricingConfigPage /></SuperadminRoute>} />
        <Route path="superadmin/logs" element={<SuperadminRoute><ApplicationLogsPage /></SuperadminRoute>} />
        <Route path="superadmin/referrals" element={<SuperadminRoute><ReferralManagementPage /></SuperadminRoute>} />
        
        {/* Legacy redirects for bookmarks */}
        <Route path="team" element={<Navigate to="/people/content-team" replace />} />
        <Route path="people/groups" element={<Navigate to="/people/employees" replace />} />
        <Route path="people/usage" element={<Navigate to="/people/adoption" replace />} />
        <Route path="usage" element={<Navigate to="/people/adoption" replace />} />
        <Route path="feedback" element={<Navigate to="/conversations/feedback" replace />} />
        <Route path="conversations" element={<Navigate to="/conversations/activity" replace />} />
        <Route path="knowledge-base" element={<Navigate to="/kb/content" replace />} />
        <Route path="kb-management" element={<Navigate to="/kb/content" replace />} />
        <Route path="analytics/*" element={<Navigate to="/overview" replace />} />
      </Route>

      {/* Catch all - 404 page */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AnalyticsStreamProvider>
              <AnalyticsToastProvider>
                <AppRoutes />
                <Toaster position="bottom-right" richColors />
              </AnalyticsToastProvider>
            </AnalyticsStreamProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
